import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import {
  CalendarIcon,
  Clock,
  Video,
  UserPlus,
  X,
  Mail,
  Loader2,
  Copy,
  Check,
  Link,
  Sparkles,
  RefreshCw,
  Lock,
  Eye,
  EyeOff,
  ShieldCheck,
  ArrowLeft,
  CalendarClock,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { z } from "zod";
import { sendMeetingInvitation } from "@/services/meetingInvitations";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { generateMeetingCode, getMeetingLink } from "@/lib/meetingUtils";
import { hashPassword, generateStrongPassword } from "@/lib/security";
import { useTranslation } from "react-i18next";

interface Participant {
  email: string;
  name?: string;
}

export interface Meeting {
  id: string;
  title: string;
  description?: string;
  dateTime: Date;
  durationMinutes?: number;
  isInstant: boolean;
  participants?: Participant[];
  meeting_code?: string;
}

export default function CreateMeeting() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const emailSchema = z.string().email(t("createMeeting.errors.emailInvalid"));
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState<Date>();
  const [time, setTime] = useState("09:00");
  const [duration, setDuration] = useState<string>("30");
  const [startNow, setStartNow] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSendingInvites, setIsSendingInvites] = useState(false);
  const [scheduledMeeting, setScheduledMeeting] = useState<Meeting | null>(null);
  const [copied, setCopied] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [meetingCode, setMeetingCode] = useState(() => generateMeetingCode());
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordEnabled, setPasswordEnabled] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const generatePassword = () => generateStrongPassword(8);

  const handlePasswordToggle = (enabled: boolean) => {
    setPasswordEnabled(enabled);
    if (enabled && !password) {
      setPassword(generatePassword());
    }
  };

  const regeneratePassword = () => {
    setPassword(generatePassword());
    toast.success(t("createMeeting.passwordGenerated"));
  };

  const regenerateMeetingCode = () => {
    setMeetingCode(generateMeetingCode());
    toast.success(t("createMeeting.codeGenerated"));
  };

  const copyMeetingLink = async (code?: string) => {
    const link = getMeetingLink(code || meetingCode);
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success(t("createMeeting.linkCopied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("createMeeting.linkCopyFailed"));
    }
  };

  const validateForm = (isScheduling: boolean) => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = t("createMeeting.errors.titleRequired");
    }

    if (isScheduling && !date) {
      newErrors.date = t("createMeeting.errors.dateRequired");
    }

    if (isScheduling && date) {
      const [hours, minutes] = time.split(":").map(Number);
      const selectedDateTime = new Date(date);
      selectedDateTime.setHours(hours, minutes, 0, 0);

      if (selectedDateTime <= new Date()) {
        newErrors.dateTime = t("createMeeting.errors.dateTimeFuture");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addParticipant = () => {
    const trimmedEmail = newEmail.trim().toLowerCase();

    if (!trimmedEmail) {
      setEmailError(t("createMeeting.errors.emailRequired"));
      return;
    }

    const result = emailSchema.safeParse(trimmedEmail);
    if (!result.success) {
      setEmailError(t("createMeeting.errors.emailInvalid"));
      return;
    }

    if (participants.some((p) => p.email === trimmedEmail)) {
      setEmailError(t("createMeeting.errors.emailDuplicate"));
      return;
    }

    setParticipants([
      ...participants,
      { email: trimmedEmail, name: newName.trim() || undefined },
    ]);
    setNewEmail("");
    setNewName("");
    setEmailError("");
  };

  const removeParticipant = (email: string) => {
    setParticipants(participants.filter((p) => p.email !== email));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addParticipant();
    }
  };

  const createMeetingInDb = async (isInstant: boolean) => {
    if (!user) {
      toast.error(t("createMeeting.errors.loginRequired"));
      return null;
    }

    let meetingDateTime: Date;
    let scheduledEnd: Date | null = null;

    if (isInstant) {
      meetingDateTime = new Date();
    } else {
      if (!date) return null;
      const [hours, minutes] = time.split(":").map(Number);
      meetingDateTime = new Date(date);
      meetingDateTime.setHours(hours, minutes, 0, 0);
    }

    if (duration) {
      scheduledEnd = new Date(meetingDateTime.getTime() + parseInt(duration) * 60 * 1000);
    }

    let hashedPassword: string | null = null;
    if (passwordEnabled && password) {
      try {
        hashedPassword = await hashPassword(password);
      } catch (error) {
        console.error("Error hashing password:", error);
        toast.error(t("createMeeting.errors.passwordSetFailed"));
        return null;
      }
    }

    const { data, error } = await supabase
      .from("meetings")
      .insert({
        title: title.trim(),
        description: description.trim() || null,
        host_id: user.id,
        meeting_code: meetingCode,
        is_active: isInstant,
        scheduled_start: meetingDateTime.toISOString(),
        scheduled_end: scheduledEnd?.toISOString() || null,
        password: hashedPassword,
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating meeting:", error);
      toast.error(t("createMeeting.errors.createFailed"));
      return null;
    }

    return {
      ...data,
      dateTime: meetingDateTime,
      durationMinutes: duration ? parseInt(duration) : undefined,
      isInstant,
      participants: participants.length > 0 ? participants : undefined,
    };
  };

  const sendInvitationEmails = async (
    meetingId: string,
    code: string,
    meetingTitle: string,
    meetingDateTime: Date,
    meetingDescription?: string
  ) => {
    if (participants.length === 0) return;

    const hostName = user?.email?.split("@")[0] || t("createMeeting.hostNameFallback");
    const scheduledDate = format(meetingDateTime, "PPP");
    const scheduledTime = format(meetingDateTime, "p");

    const invitePromises = participants.map((participant) =>
      sendMeetingInvitation({
        recipientEmail: participant.email,
        recipientName: participant.name,
        meetingTitle,
        meetingCode: code,
        hostName,
        scheduledDate,
        scheduledTime,
        description: meetingDescription,
        hasPassword: passwordEnabled && !!password,
      }).catch((error) => {
        console.error(`Failed to send invite to ${participant.email}:`, error);
        return { error: true, email: participant.email };
      })
    );

    const results = await Promise.all(invitePromises);
    const failed = results.filter((r) => r && typeof r === "object" && "error" in r);

    if (failed.length === 0) {
      toast.success(t("createMeeting.invitesSent", { count: participants.length }));
    } else if (failed.length < participants.length) {
      toast.warning(t("createMeeting.invitesPartial", { failed: failed.length, total: participants.length }));
    } else {
      toast.error(t("createMeeting.invitesFailed"));
    }
  };

  const handleStartNow = async () => {
    if (!validateForm(false)) return;
    if (!user) {
      toast.error(t("createMeeting.errors.loginPrompt"));
      navigate("/auth");
      return;
    }

    setIsSendingInvites(true);

    const meeting = await createMeetingInDb(true);
    if (!meeting) {
      setIsSendingInvites(false);
      return;
    }

    if (participants.length > 0) {
      await sendInvitationEmails(meeting.id, meeting.meeting_code, meeting.title, meeting.dateTime, meeting.description || undefined);
    }

    setIsSendingInvites(false);

    toast.success(t("createMeeting.meetingCreated"));
    navigate(`/meeting/${meeting.id}`);
  };

  const handleSchedule = async () => {
    if (!validateForm(true)) return;
    if (!user) {
      toast.error(t("createMeeting.errors.loginSchedulePrompt"));
      navigate("/auth");
      return;
    }

    setIsSendingInvites(true);

    const meeting = await createMeetingInDb(false);
    if (!meeting) {
      setIsSendingInvites(false);
      return;
    }

    if (participants.length > 0) {
      await sendInvitationEmails(meeting.id, meeting.meeting_code, meeting.title, meeting.dateTime, meeting.description || undefined);
    }

    setIsSendingInvites(false);

    setScheduledMeeting({
      id: meeting.id,
      title: meeting.title,
      description: meeting.description || undefined,
      dateTime: meeting.dateTime,
      durationMinutes: meeting.durationMinutes,
      isInstant: false,
      participants: meeting.participants,
      meeting_code: meeting.meeting_code,
    });

    toast.success(
      t("createMeeting.scheduledSuccess", {
        date: format(meeting.dateTime, "PPP 'at' p"),
      })
    );
  };

  const activateScheduledMeeting = async () => {
    if (!scheduledMeeting) return;

    setIsActivating(true);

    const { error } = await supabase
      .from("meetings")
      .update({
        is_active: true,
        scheduled_start: new Date().toISOString(),
      })
      .eq("id", scheduledMeeting.id);

    setIsActivating(false);

    if (error) {
      console.error("Error activating meeting:", error);
      toast.error(t("createMeeting.errors.startFailed"));
      return;
    }

    toast.success(t("createMeeting.started"));
    navigate(`/meeting/${scheduledMeeting.id}`);
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setDate(undefined);
    setTime("09:00");
    setDuration("30");
    setStartNow(false);
    setParticipants([]);
    setScheduledMeeting(null);
    setMeetingCode(generateMeetingCode());
    setPasswordEnabled(false);
    setPassword("");
  };

  const checklist = [
    title.trim() ? "Title added" : "Add a clear meeting title",
    startNow ? "Starts immediately" : date ? "Schedule selected" : "Choose a schedule if not starting now",
    participants.length > 0 ? `${participants.length} invitee${participants.length > 1 ? "s" : ""} ready` : "Invite teammates if needed",
    passwordEnabled ? "Passcode enabled" : "Optional passcode is off",
  ];

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,hsl(var(--primary)/0.09),transparent_35%),linear-gradient(180deg,hsl(var(--background)),hsl(var(--muted)/0.22))] px-4 py-6 lg:px-8 lg:py-10">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="flex items-start gap-4">
            <Button variant="outline" size="icon" onClick={() => navigate("/dashboard")} className="rounded-full bg-background/80">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Host workspace</p>
              <h1 className="mt-2 text-3xl font-semibold tracking-tight">{t("createMeeting.title")}</h1>
              <p className="mt-2 max-w-2xl text-muted-foreground">{t("createMeeting.subtitle")}</p>
            </div>
          </div>
          <div className="rounded-2xl border border-border/60 bg-background/80 px-4 py-3 shadow-sm">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Current mode</p>
            <p className="mt-1 text-base font-semibold">{startNow ? "Instant room" : "Scheduled room"}</p>
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
          <div className="order-2 xl:order-1">
            <Card className="border-border/60 bg-card/95 shadow-sm">
              <CardHeader className="space-y-4 border-b border-border/60 pb-6">
                <div className="flex justify-center xl:justify-start">
                  <div className="rounded-2xl bg-primary p-4 text-primary-foreground shadow-lg shadow-primary/20">
                    <Video className="h-9 w-9" />
                  </div>
                </div>
                <div className="space-y-2 text-center xl:text-left">
                  <CardTitle className="text-2xl sm:text-3xl font-bold">{t("createMeeting.title")}</CardTitle>
                  <CardDescription className="text-sm sm:text-base text-muted-foreground">
                    Set the room, protect it if needed, invite people, and launch when you are ready.
                  </CardDescription>
                </div>
              </CardHeader>

              <CardContent className="space-y-6 px-4 py-6 sm:px-6">
                {scheduledMeeting ? (
                  <div className="space-y-6">
                    <div className="rounded-3xl border border-primary/20 bg-primary/5 p-6 text-center">
                      <div className="mx-auto mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                        <Check className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-xl font-semibold">{t("createMeeting.scheduledTitle")}</h3>
                      <p className="mt-2 text-muted-foreground">
                        {t("createMeeting.scheduledDetails", {
                          title: scheduledMeeting.title,
                          date: format(new Date(scheduledMeeting.dateTime), "PPP 'at' p"),
                        })}
                      </p>
                    </div>

                    <div className="grid gap-4 rounded-2xl border border-border/60 bg-muted/25 p-4 md:grid-cols-[1.4fr_0.6fr]">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <Link className="h-4 w-4" />
                          {t("createMeeting.meetingLinkLabel")}
                        </div>
                        <div className="flex gap-2">
                          <Input
                            readOnly
                            value={getMeetingLink(scheduledMeeting.meeting_code || "")}
                            className="bg-background font-mono text-sm"
                          />
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => copyMeetingLink(scheduledMeeting.meeting_code)}
                            className="shrink-0"
                            title={t("createMeeting.copyLink")}
                          >
                            {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => {
                              const link = getMeetingLink(scheduledMeeting.meeting_code || "");
                              const subject = encodeURIComponent(t("createMeeting.emailSubject", { title: scheduledMeeting.title }));
                              const body = encodeURIComponent(
                                scheduledMeeting.description
                                  ? t("createMeeting.emailBodyWithDescription", {
                                      title: scheduledMeeting.title,
                                      date: format(new Date(scheduledMeeting.dateTime), "PPP 'at' p"),
                                      description: scheduledMeeting.description,
                                      link,
                                    })
                                  : t("createMeeting.emailBody", {
                                      title: scheduledMeeting.title,
                                      date: format(new Date(scheduledMeeting.dateTime), "PPP 'at' p"),
                                      link,
                                    })
                              );
                              window.open(`mailto:?subject=${subject}&body=${body}`, "_blank");
                            }}
                            className="shrink-0"
                            title={t("createMeeting.shareEmail")}
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">{t("createMeeting.shareLinkHelp")}</p>
                      </div>

                      <div className="rounded-2xl border border-dashed border-border bg-background/70 p-4">
                        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Start checklist</p>
                        <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                          <li>Share the link with guests</li>
                          <li>Start when you are ready</li>
                          <li>Return to dashboard if you need more prep</li>
                        </ul>
                      </div>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row">
                      <Button onClick={activateScheduledMeeting} className="w-full sm:flex-1" disabled={isActivating}>
                        {isActivating ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            {t("createMeeting.starting")}
                          </>
                        ) : (
                          <>
                            <Video className="h-4 w-4 mr-2" />
                            {t("createMeeting.startNow")}
                          </>
                        )}
                      </Button>
                      <Button variant="outline" onClick={resetForm} className="w-full sm:flex-1">
                        {t("createMeeting.createAnother")}
                      </Button>
                      <Button onClick={() => navigate("/dashboard")} className="w-full sm:flex-1">
                        {t("createMeeting.goToDashboard")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid gap-4 rounded-2xl border border-primary/20 bg-primary/5 p-4 lg:grid-cols-[1.15fr_0.85fr]">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-primary" />
                            <span className="text-sm font-medium text-foreground">{t("createMeeting.meetingCodeTitle")}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={regenerateMeetingCode}
                            className="h-8 px-2 text-muted-foreground hover:text-foreground"
                          >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            {t("createMeeting.newCode")}
                          </Button>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="flex-1 rounded-xl border bg-background p-3">
                            <p className="text-xl font-mono font-bold text-center tracking-wider text-foreground sm:text-2xl">
                              {meetingCode}
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => copyMeetingLink()}
                            className="h-12 w-12 shrink-0"
                            title={t("createMeeting.copyMeetingLink")}
                          >
                            {copied ? <Check className="h-5 w-5 text-primary" /> : <Copy className="h-5 w-5" />}
                          </Button>
                        </div>

                        <p className="truncate text-xs text-muted-foreground">{getMeetingLink(meetingCode)}</p>
                      </div>

                      <div className="rounded-2xl border border-dashed border-border bg-background/80 p-4">
                        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Room setup</p>
                        <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                          {checklist.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="title" className="text-sm font-medium">
                        {t("createMeeting.meetingTitle")} <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="title"
                        placeholder={t("createMeeting.meetingTitlePlaceholder")}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className={cn("h-11", errors.title && "border-destructive focus-visible:ring-destructive")}
                      />
                      {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description" className="text-sm font-medium">
                        {t("createMeeting.descriptionLabel")} <span className="text-muted-foreground">{t("createMeeting.optionalLabel")}</span>
                      </Label>
                      <Textarea
                        id="description"
                        placeholder={t("createMeeting.descriptionPlaceholder")}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={3}
                        className="resize-none"
                      />
                    </div>

                    <div className="grid gap-4 lg:grid-cols-2">
                      <div className="space-y-4 rounded-2xl border border-border bg-card p-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label htmlFor="start-now" className="text-base font-medium cursor-pointer">
                              {t("createMeeting.startImmediately")}
                            </Label>
                            <p className="text-sm text-muted-foreground">{t("createMeeting.startImmediatelyDesc")}</p>
                          </div>
                          <Switch id="start-now" checked={startNow} onCheckedChange={setStartNow} />
                        </div>

                        <div className="rounded-xl border border-dashed border-border bg-muted/20 p-3 text-sm text-muted-foreground">
                          {startNow
                            ? "The meeting becomes active as soon as you create it."
                            : "Keep it scheduled until you're ready to launch from the success state."}
                        </div>
                      </div>

                      <div className="space-y-4 rounded-2xl border border-border bg-card p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Lock className="h-4 w-4 text-primary" />
                            <div className="space-y-0.5">
                              <Label htmlFor="password-toggle" className="text-base font-medium cursor-pointer">
                                {t("createMeeting.passwordProtection")}
                              </Label>
                              <p className="text-sm text-muted-foreground">{t("createMeeting.passwordProtectionDesc")}</p>
                            </div>
                          </div>
                          <Switch id="password-toggle" checked={passwordEnabled} onCheckedChange={handlePasswordToggle} />
                        </div>

                        {passwordEnabled && (
                          <div className="space-y-3 border-t pt-3">
                            <div className="flex items-center gap-2">
                              <div className="relative flex-1">
                                <Input
                                  id="meeting-password"
                                  type={showPassword ? "text" : "password"}
                                  value={password}
                                  onChange={(e) => setPassword(e.target.value)}
                                  placeholder={t("createMeeting.passwordPlaceholder")}
                                  className="h-11 pr-10 font-mono"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="absolute right-0 top-0 h-11 w-11 text-muted-foreground hover:text-foreground"
                                  onClick={() => setShowPassword(!showPassword)}
                                >
                                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={regeneratePassword}
                                className="h-11 w-11 shrink-0"
                                title={t("createMeeting.generatePassword")}
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <ShieldCheck className="h-3.5 w-3.5" />
                              <span>{t("createMeeting.passwordHint")}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {!startNow && (
                      <div className="space-y-4 rounded-2xl border border-border bg-card p-4">
                        <h3 className="flex items-center gap-2 font-medium text-foreground">
                          <CalendarClock className="h-4 w-4 text-primary" />
                          {t("createMeeting.scheduleDetails")}
                        </h3>

                        <div className="grid gap-4 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">
                              {t("createMeeting.dateLabel")} <span className="text-destructive">*</span>
                            </Label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  className={cn(
                                    "h-11 w-full justify-start text-left font-normal",
                                    !date && "text-muted-foreground",
                                    errors.date && "border-destructive"
                                  )}
                                >
                                  <CalendarIcon className="mr-2 h-4 w-4" />
                                  {date ? format(date, "PPP") : t("createMeeting.pickDate")}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={date}
                                  onSelect={setDate}
                                  disabled={(value) => value < new Date(new Date().setHours(0, 0, 0, 0))}
                                  initialFocus
                                  className="pointer-events-auto"
                                />
                              </PopoverContent>
                            </Popover>
                            {errors.date && <p className="text-sm text-destructive">{errors.date}</p>}
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="time" className="text-sm font-medium">
                              {t("createMeeting.timeLabel")} <span className="text-destructive">*</span>
                            </Label>
                            <Input id="time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className="h-11" />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="duration" className="text-sm font-medium">
                            {t("createMeeting.durationLabel")} <span className="text-muted-foreground">{t("createMeeting.optionalLabel")}</span>
                          </Label>
                          <Select value={duration} onValueChange={setDuration}>
                            <SelectTrigger id="duration" className="h-11">
                              <SelectValue placeholder={t("createMeeting.durationPlaceholder")} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="15">{t("createMeeting.duration15")}</SelectItem>
                              <SelectItem value="30">{t("createMeeting.duration30")}</SelectItem>
                              <SelectItem value="60">{t("createMeeting.duration60")}</SelectItem>
                              <SelectItem value="90">{t("createMeeting.duration90")}</SelectItem>
                              <SelectItem value="120">{t("createMeeting.duration120")}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {errors.dateTime && <p className="text-sm text-destructive">{errors.dateTime}</p>}
                      </div>
                    )}

                    <div className="space-y-4 rounded-2xl border border-border bg-card p-4">
                      <h3 className="font-medium flex items-center gap-2 text-foreground">
                        <UserPlus className="h-4 w-4 text-primary" />
                        {t("createMeeting.inviteParticipants")} <span className="text-muted-foreground font-normal">{t("createMeeting.optionalLabel")}</span>
                      </h3>
                      <p className="text-sm text-muted-foreground">{t("createMeeting.inviteDesc")}</p>

                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5">
                          <Label htmlFor="participant-email" className="text-sm font-medium">
                            {t("createMeeting.emailAddressLabel")}
                          </Label>
                          <Input
                            id="participant-email"
                            type="email"
                            placeholder={t("createMeeting.emailPlaceholder")}
                            value={newEmail}
                            onChange={(e) => {
                              setNewEmail(e.target.value);
                              setEmailError("");
                            }}
                            onKeyDown={handleKeyDown}
                            className={cn("h-11", emailError && "border-destructive")}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="participant-name" className="text-sm font-medium">
                            {t("createMeeting.nameLabel")} <span className="text-muted-foreground">{t("createMeeting.optionalLabel")}</span>
                          </Label>
                          <Input
                            id="participant-name"
                            type="text"
                            placeholder={t("createMeeting.namePlaceholder")}
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="h-11"
                          />
                        </div>
                      </div>

                      {emailError && <p className="text-sm text-destructive">{emailError}</p>}

                      <Button type="button" variant="outline" size="sm" onClick={addParticipant} className="w-full sm:w-auto">
                        <UserPlus className="h-4 w-4 mr-2" />
                        {t("createMeeting.addParticipant")}
                      </Button>

                      {participants.length > 0 && (
                        <div className="space-y-2">
                          <Label className="text-sm text-muted-foreground">
                            {t("createMeeting.inviteCount", { count: participants.length })}
                          </Label>
                          <div className="flex flex-wrap gap-2">
                            {participants.map((participant) => (
                              <Badge key={participant.email} variant="secondary" className="flex items-center gap-1.5 py-1.5 px-3">
                                <Mail className="h-3 w-3" />
                                <span className="max-w-[200px] truncate">{participant.name || participant.email}</span>
                                {participant.name && <span className="text-muted-foreground text-xs">({participant.email})</span>}
                                <button
                                  type="button"
                                  onClick={() => removeParticipant(participant.email)}
                                  className="ml-1 hover:text-destructive transition-colors"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-2">
                      {startNow ? (
                        <Button onClick={handleStartNow} className="w-full sm:flex-1 h-12 text-base font-medium" size="lg" disabled={isSendingInvites}>
                          {isSendingInvites ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              {t("createMeeting.creatingMeeting")}
                            </>
                          ) : (
                            <>
                              <Video className="h-4 w-4 mr-2" />
                              {t("createMeeting.createAndStart")}
                            </>
                          )}
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            onClick={handleStartNow}
                            className="w-full sm:flex-1 h-12"
                            size="lg"
                            disabled={isSendingInvites}
                          >
                            {isSendingInvites ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                {t("createMeeting.creating")}
                              </>
                            ) : (
                              t("createMeeting.startNowInstead")
                            )}
                          </Button>
                          <Button onClick={handleSchedule} className="w-full sm:flex-1 h-12 text-base font-medium" size="lg" disabled={isSendingInvites}>
                            {isSendingInvites ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                {t("createMeeting.scheduling")}
                              </>
                            ) : (
                              <>
                                <CalendarIcon className="h-4 w-4 mr-2" />
                                {t("createMeeting.scheduleMeeting")}
                              </>
                            )}
                          </Button>
                        </>
                      )}
                    </div>

                    <Button variant="ghost" onClick={() => navigate("/dashboard")} className="w-full text-muted-foreground hover:text-foreground">
                      {t("createMeeting.backToDashboard")}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="order-1 space-y-6 xl:order-2">
            <Card className="border-border/60 bg-background/90 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Meeting preview</CardTitle>
                <CardDescription>See the setup users will receive before you create the room.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Join link</p>
                  <p className="mt-2 break-all font-mono text-sm">{getMeetingLink(meetingCode)}</p>
                </div>
                <div className="grid gap-3">
                  <div className="rounded-2xl border border-border/60 p-4">
                    <p className="text-sm font-medium">Access</p>
                    <p className="mt-1 text-sm text-muted-foreground">{passwordEnabled ? "Protected with passcode" : "Open to anyone with the link"}</p>
                  </div>
                  <div className="rounded-2xl border border-border/60 p-4">
                    <p className="text-sm font-medium">Timing</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {startNow ? "Starts as soon as you create it." : date ? `Planned for ${format(date, "PPP")} at ${time}.` : "Schedule details appear here after you choose them."}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-border/60 p-4">
                    <p className="text-sm font-medium">Invites</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {participants.length > 0 ? `${participants.length} recipient${participants.length > 1 ? "s" : ""} will be notified.` : "No invitees added yet."}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
